/**
 * Normalizes pathname for page_view comparison and payloads: removes a trailing
 * slash except for the root path "/".
 */
export function normalizePathnameForPageview(pathname: string): string {
  if (pathname.length > 1 && pathname.endsWith('/')) {
    return pathname.slice(0, -1);
  }
  return pathname;
}

/**
 * Builds a stable search string: same key/value pairs in any order compare equal.
 * Preserves duplicate keys (order among pairs is sorted; repeated keys stay grouped by sort order).
 */
export function canonicalizeSearch(search: string): string {
  if (!search || search === '?') {
    return '';
  }
  const raw = search.startsWith('?') ? search.slice(1) : search;
  if (!raw) {
    return '';
  }
  const params = new URLSearchParams(raw);
  const pairs: [string, string][] = [];
  params.forEach((value, key) => {
    pairs.push([key, value]);
  });
  pairs.sort(([k1, v1], [k2, v2]) => {
    const c = k1.localeCompare(k2);
    return c !== 0 ? c : v1.localeCompare(v2);
  });
  const next = new URLSearchParams();
  for (const [k, v] of pairs) {
    next.append(k, v);
  }
  const s = next.toString();
  return s === '' ? '' : `?${s}`;
}

/**
 * Identity for autocapture `page_view` deduplication: same logical URL (any framework)
 * maps to one key. Uses origin + normalized pathname + canonical query + hash.
 */
export function getPageviewDedupeKey(
  location: Pick<Location, 'origin' | 'pathname' | 'search' | 'hash'>,
): string {
  const path = normalizePathnameForPageview(location.pathname);
  const search = canonicalizeSearch(location.search);
  return `${location.origin}${path}${search}${location.hash}`;
}
